import i18n from 'i18next';

const dateOpts = { month: 'short', year: 'numeric', day: 'numeric' } as const;
const longMonthDateOpts = { month: 'long', year: 'numeric', day: 'numeric' } as const;
const longMonthDateTimeOpts = {
  month: 'long',
  year: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
} as const;
const timeOpts = { hour: 'numeric', minute: 'numeric' } as const;
const norweiganTimeZone = 'Europe/Oslo';

/**
 * Tar et API-tidsstempel (E.g. "20211220-112849-315210") og tolker det som UTC,
 * men returnerer et Date-objekt med korrekt lokalt klokkeslett.
 * Bruk denne hvis du vet at API-tidsstempelet er i UTC.
 * @param timestamp Tidsstempelet som skal konverteres
 * @return Date-objekt i lokal tid
 */
export function apiTimestampToLocalDate(timestamp?: string): Date | null {
  if (!timestamp || !timestamp.match(/^[0-9]{8}-[0-9]{6}-[0-9]{6}$/)) return null;

  const [date, time, microseconds] = timestamp.split('-');
  const year = Number(date.substring(0, 4));
  const month = Number(date.substring(4, 6)) - 1;
  const day = Number(date.substring(6, 8));
  const hour = Number(time.substring(0, 2));
  const minute = Number(time.substring(2, 4));
  const second = Number(time.substring(4, 6));

  // Tolker som UTC, men returnerer lokal tid
  const utcMillis = Date.UTC(year, month, day, hour, minute, second, Number(microseconds) / 1000);
  return new Date(utcMillis);
}

/**
 * Takes a valid timestamp from the DB (E.g. "20211220-112849-315210") and converts it to a Date-object.
 * @param timestamp The timestamp to convert
 * @return The created Date-instance or null, if the timestamp is invalid
 */
export function apiTimestampToDate(timestamp?: string): Date | null {
  if (!timestamp || !timestamp.match(/^[0-9]{8}-[0-9]{6}-[0-9]{6}$/)) return null;

  const [date, time, microseconds] = timestamp.split('-');
  const year = Number(date.substring(0, 4));
  const month = Number(date.substring(4, 6)) - 1;
  const day = Number(date.substring(6, 8));
  const hour = Number(time.substring(0, 2));
  const minute = Number(time.substring(2, 4));
  const second = Number(time.substring(4, 6));

  // Use local time, not UTC
  return new Date(year, month, day, hour, minute, second, Number(microseconds) / 1000);
}

/**
 * Compares an API timestamp against a reference date.
 * @param timestamp The API timestamp to compare
 * @param referenceDate The reference date, defaults to now
 * @return TRUE when the parsed API timestamp is at or after the reference date
 */
export function isApiTimestampAtOrAfter(
  timestamp?: string,
  referenceDate: Date | string | number = new Date(),
): boolean {
  const parsedTimestamp = apiTimestampToDate(timestamp);
  const reference = new Date(referenceDate);

  if (!parsedTimestamp || Number.isNaN(reference.getTime())) {
    return false;
  }

  return parsedTimestamp.getTime() >= reference.getTime();
}

/**
 * Returns the current wall-clock date/time in Norway (Europe/Oslo) as a local Date instance.
 * This is useful when API timestamps are stored as local datetimes without an explicit timezone.
 */
export function getCurrentDateTimeInNorway(now: Date = new Date()): Date {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: norweiganTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const dateParts = Object.fromEntries(formatter.formatToParts(now).map(({ type, value }) => [type, value]));

  return new Date(
    Number(dateParts.year),
    Number(dateParts.month) - 1,
    Number(dateParts.day),
    Number(dateParts.hour),
    Number(dateParts.minute),
    Number(dateParts.second),
    now.getMilliseconds(),
  );
}

/**
 * Converts a Date to the datetime-format the API expects
 * @param date
 */
export function dateToApiTimestamp(date: Date | string | number): string | null {
  const dateInstance = new Date(date);
  if (dateInstance.toString() === 'Invalid Date') {
    return null;
  }
  const year = dateInstance.getFullYear();
  const month = String(dateInstance.getMonth() + 1).padStart(2, '0');
  const day = String(dateInstance.getDate()).padStart(2, '0');
  const hour = String(dateInstance.getHours()).padStart(2, '0');
  const minute = String(dateInstance.getMinutes()).padStart(2, '0');
  const second = String(dateInstance.getSeconds()).padStart(2, '0');
  const millisecond = String(dateInstance.getMilliseconds()).padEnd(6, '0');
  return `${year}${month}${day}-${hour}${minute}${second}-${millisecond}`;
}

/**
 * Get the mode (date, time or datetime) for displaying date, based on the actual timestamp.
 *
 * - <code>time</code> is used when sometime "today"
 * - <code>datetime</code> is used when sometime during the last week, or in the future
 * - <code>date</code> is used when over one week ago
 *
 * @param thenDate
 */
export function getTimestampMode(thenDate?: Date): 'date' | 'datetime' | 'time' {
  const now = new Date();
  const then = thenDate || now;
  const diff = now.getTime() - then.getTime();
  if (diff > 7 * 24 * 60 * 60 * 1000) {
    // over one week ago
    return 'date';
  }
  if (
    now.getUTCFullYear() === then.getUTCFullYear() &&
    now.getUTCMonth() === then.getUTCMonth() &&
    now.getUTCDate() === then.getUTCDate()
  ) {
    // Sometime today
    // return 'datetime';
    return 'datetime';
  }
  return 'datetime';
}

/**
 * Som formatAPITimestamp, men tolker API-tidsstempel som UTC og viser korrekt lokal tid.
 * @param timestamp Tidsstempelet fra API
 * @param format Format for visning ('auto', 'date', 'time', 'datetime', 'longdate', 'longdatetime')
 * @return Formatert streng eller null
 */
export function formatAPITimestampLocal(
  timestamp?: string,
  format: 'auto' | 'date' | 'time' | 'datetime' | 'longdate' | 'longdatetime' = 'auto',
): string | null {
  const d = apiTimestampToLocalDate(timestamp);
  if (!d) return null;
  const mode = format === 'auto' ? getTimestampMode(d) : format;
  if (mode === 'date') return d.toLocaleDateString(i18n.language, dateOpts);
  if (mode === 'longdate') return d.toLocaleDateString(i18n.language, longMonthDateOpts);
  if (mode === 'longdatetime') return d.toLocaleString(i18n.language, longMonthDateTimeOpts);
  if (mode === 'time') return d.toLocaleTimeString(i18n.language, timeOpts);
  return d.toLocaleString(i18n.language, { ...dateOpts, ...timeOpts });
}

/**
 * Takes a timestamp from the DB (E.g. "20211220-112849-315210") and turns it into a human-readable format
 * @param timestamp The timestamp to parse
 * @param format What format the result should be in
 * @return The parsed timestamp or null if the timestamp is invalid
 */
export function formatAPITimestamp(
  timestamp?: string,
  format: 'auto' | 'date' | 'time' | 'datetime' | 'longdate' | 'longdatetime' = 'auto',
): string | null {
  const d = apiTimestampToDate(timestamp);
  if (!d) return null;
  const mode = format === 'auto' ? getTimestampMode(d) : format;
  if (mode === 'date') return d.toLocaleDateString(i18n.language, dateOpts);
  if (mode === 'longdate') return d.toLocaleDateString(i18n.language, longMonthDateOpts);
  if (mode === 'longdatetime') return d.toLocaleString(i18n.language, longMonthDateTimeOpts);
  if (mode === 'time') return d.toLocaleTimeString(i18n.language, timeOpts);
  return d.toLocaleString(i18n.language, { ...dateOpts, ...timeOpts });
}

/**
 *
 * @param timestamp
 */
export function formatISOString(timestamp?: string): string | null {
  if (!timestamp) return null;
  const [date, t] = timestamp.split('T');
  const [year, month, day] = date.split('-');
  const [time] = t.split('+');
  const [hour, minute, second] = time.split(':');
  const dateObj = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
  const mode = getTimestampMode(dateObj);
  if (mode === 'date') return dateObj.toLocaleDateString(i18n.language, dateOpts);
  if (mode === 'time') return dateObj.toLocaleTimeString(i18n.language, timeOpts);
  return dateObj.toLocaleString(i18n.language, { ...dateOpts, ...timeOpts });
}
